import * as React from 'react';
import { Button } from '@mui/material';
import Stack from '@mui/system/Stack';
import { getGridDateOperators, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import type Project from 'benchmarking-domain/project';
import { RenderForRole } from 'components/auth/render-for-roles';
import { DataGridInMemory } from 'components/core/shared/data-grid-in-memory';
import { Timestamp } from 'firebase/firestore';
import { nameOf } from 'helpers/nameof';
import { paths } from 'paths';
import { useNavigate } from 'react-router-dom';

import { ProjectArchiveButton } from './project-archive-confirmation';

const ProjectArchiveButtonMemo = React.memo(ProjectArchiveButton);

interface ProjectsTableProps {
  rows?: Project[];
}

export function ProjectsTable({ rows = [] }: ProjectsTableProps): React.JSX.Element {
  const navigate = useNavigate();

  const dataTimeFrameFieldName = 'data-timeframe';

  const columns: GridColDef<Project>[] = [
    {
      field: nameOf<Project>('name'),
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: nameOf<Project>('plant'),
      headerName: 'Plant',
      flex: 1,
      minWidth: 150,
    },
    {
      field: dataTimeFrameFieldName,
      headerName: 'Data timeframe',
      flex: 1,
      minWidth: 150,
      filterOperators: getGridDateOperators(),
      valueGetter: (_, project) => new Date(project.endYear, project.endMonth - 1),
      valueFormatter: (_, project) =>
        `${project.startMonth}/${project.startYear} - ${project.endMonth}/${project.endYear}`,
    },
    {
      field: nameOf<Project>('technology'),
      headerName: 'Technology',
      flex: 1,
      minWidth: 150,
    },
    {
      field: nameOf<Project>('createdBy'),
      headerName: 'Created by',
      flex: 1,
      minWidth: 150,
    },
    {
      field: nameOf<Project>('creationTimestamp'),
      headerName: 'Created date',
      flex: 1,
      minWidth: 150,
      filterOperators: getGridDateOperators(),
      valueGetter: (value?: Timestamp) => value?.toDate(),
      valueFormatter: (value?: Date) => value?.toLocaleString(),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      minWidth: 300,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Project>) => {
        return (
          <Stack direction="row" spacing={1} sx={{ marginY: 1 }}>
            <Button
              variant="contained"
              onClick={() => {
                navigate(paths.app.client.projects.getDetailsUrl(params.row.id));
              }}
            >
              View&nbsp;project
            </Button>
            <RenderForRole roles={['superAdmin']}>
              <ProjectArchiveButtonMemo projectId={params.row.id} />
            </RenderForRole>
          </Stack>
        );
      },
    },
  ];

  const initialState: GridInitialStateCommunity = {
    pagination: { paginationModel: { pageSize: 10 } },
    sorting: {
      sortModel: [{ field: dataTimeFrameFieldName, sort: 'desc' }],
    },
  };

  return (
    <DataGridInMemory columns={columns} rows={rows} initialState={initialState} searchPlaceholder="Search projects" />
  );
}
